import { useMemo } from 'react';
import { create } from 'zustand';
import { temporal } from 'zundo';
import { nanoid } from 'nanoid';
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  reconnectEdge,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
} from '@xyflow/react';
import type {
  BlockShape,
  ComponentData,
  ComponentMechanicalData,
  ComponentTemplate,
  DocumentSection,
  InterconnectBundle,
  Pin,
  PinSide,
  ProjectMeta,
  Requirement,
  SheetKind,
  WireData,
} from './types';
import { getTemplate } from './library';
import { isPro, useIsPro, showProNotice, FREE_LIMITS } from './entitlements';
import { showToast } from './components/Toast';

/** Reject a single embedded image whose base64 string exceeds this (~3.7 MB of
 *  actual image). Keeps one paste from bloating the autosave / cloud row and
 *  protects against pathological "gigabytes of photos" projects. */
const MAX_IMAGE_BASE64_BYTES = 5_000_000;

export type CompNode = Node<ComponentData, 'component'>;
export type WireEdge = Edge<WireData>;

export type AnnotationData = {
  text: string;
  color?: string;
  [key: string]: unknown;
};
export type AnnotationNode = Node<AnnotationData, 'annotation'>;

export type ImageData = {
  src: string;
  name?: string;
  width?: number;
  height?: number;
  [key: string]: unknown;
};
export type ImageNode = Node<ImageData, 'image'>;

export type BlockData = {
  title: string;
  subtitle?: string;
  color?: string;
  textColor?: string;
  shape?: BlockShape;
  width?: number;
  height?: number;
  [key: string]: unknown;
};
export type BlockNode = Node<BlockData, 'block'>;

export type MechanicalItemData = ComponentMechanicalData & {
  label: string;
  templateKey?: string;
  partNumber?: string;
  manufacturer?: string;
  color?: string;
  /** When true (or unset), the footprint fill is auto-assigned by part type.
   *  Set to false once the user picks a colour by hand so their choice wins. */
  autoColor?: boolean;
  scalePxPerMm?: number;
  labelOffsetX?: number;
  labelOffsetY?: number;
  /** 0 | 90 | 180 | 270 - clockwise footprint rotation on mechanical sheets. */
  rotation?: number;
  [key: string]: unknown;
};
export type MechanicalItemNode = Node<MechanicalItemData, 'mechanicalItem'>;

export type MeasurementData = {
  width: number;
  height: number;
  scalePxPerMm: number;
  /** true when the measured segment runs bottom-left → top-right, so the
   *  rendered dimension line follows the actual direction rather than the
   *  bounding-box main diagonal. */
  antiDiagonal?: boolean;
  label?: string;
  [key: string]: unknown;
};
export type MeasurementNode = Node<MeasurementData, 'measurement'>;

/** Power / ground symbol — global by name (a "GND" anywhere = same net). */
export type PowerSymbolKind = 'gnd' | 'earth' | 'vcc' | 'rail';
export type PowerSymbolData = {
  symbol: PowerSymbolKind;
  /** net name, e.g. "GND", "+24V" */
  name: string;
  [key: string]: unknown;
};
export type PowerSymbolNode = Node<PowerSymbolData, 'powerSymbol'>;

/** Net label — sheet-local (same name merges only within its own sheet). */
export type NetLabelData = { name: string; side?: PinSide; [key: string]: unknown };
export type NetLabelNode = Node<NetLabelData, 'netLabel'>;

export type SpliceData = {
  label: string;
  kind?: 'splice' | 'junction';
  notes?: string;
  [key: string]: unknown;
};
export type SpliceNode = Node<SpliceData, 'splice'>;

/** The single connection handle id shared by power symbols & net labels. */
export const NET_TERMINAL_HANDLE = 'p';

/** Connection handles on a splice node — 8 points (2 per side) so a junction
 *  can fan out to many wires. The first four keep their original ids so
 *  existing splice connections survive. */
export const SPLICE_HANDLE_IDS = [
  'p-top',
  'p-right',
  'p-bottom',
  'p-left',
  'p-top-2',
  'p-right-2',
  'p-bottom-2',
  'p-left-2',
] as const;

/** Canvas wire visibility filter (power nets like GND/+24V). */
export type WireFilter = 'all' | 'no-power' | 'power-only';

export type PendingTool = 'annotation' | 'measure' | null;

export type AnyNode =
  | CompNode
  | AnnotationNode
  | BlockNode
  | ImageNode
  | MechanicalItemNode
  | MeasurementNode
  | PowerSymbolNode
  | NetLabelNode
  | SpliceNode;

export function isCompNode(n: AnyNode): n is CompNode {
  return n.type === 'component';
}
export function isAnnotationNode(n: AnyNode): n is AnnotationNode {
  return n.type === 'annotation';
}
export function isImageNode(n: AnyNode): n is ImageNode {
  return n.type === 'image';
}
export function isBlockNode(n: AnyNode): n is BlockNode {
  return n.type === 'block';
}
export function isMechanicalItemNode(n: AnyNode): n is MechanicalItemNode {
  return n.type === 'mechanicalItem';
}
export function isMeasurementNode(n: AnyNode): n is MeasurementNode {
  return n.type === 'measurement';
}
export function isPowerSymbolNode(n: AnyNode): n is PowerSymbolNode {
  return n.type === 'powerSymbol';
}
export function isNetLabelNode(n: AnyNode): n is NetLabelNode {
  return n.type === 'netLabel';
}
export function isSpliceNode(n: AnyNode): n is SpliceNode {
  return n.type === 'splice';
}

export interface Sheet {
  id: string;
  name: string;
  kind: SheetKind;
  nodes: AnyNode[];
  edges: WireEdge[];
  /** Only used when kind === 'requirements'. */
  requirements?: Requirement[];
  /** Only used when kind === 'interconnect'. */
  bundles?: InterconnectBundle[];
  /** Only used when kind === 'document'. Ordered list of sections. */
  sections?: DocumentSection[];
  /** Only used when kind === 'document'. Optional category, e.g. "Maintenance",
   *  "Installation", "Troubleshooting". */
  documentCategory?: string;
}

interface StoreState {
  meta: ProjectMeta;
  sheets: Sheet[];
  activeSheetId: string;
  selectedNodeId: string | null;
  selectedEdgeId: string | null;
  /** When set, the canvas dims every wire that does NOT belong to this
   *  harness. Lets the user inspect a single cable run without the rest of
   *  the harness getting in the way. Cleared by pressing Esc / clicking the
   *  active harness focus chip again. */
  focusedHarnessId: string | null;
  /** Net highlight: when set, the canvas dims every wire not belonging to
   *  the focused net. Computed by `focusNet` from the connectivity model. */
  focusedNet: { id: string; edgeIds: string[]; nodeIds: string[] } | null;
  focusedNetMode: 'dim' | 'solo';
  /** Canvas filter to hide power wires (or show only power). */
  wireFilter: WireFilter;
  setWireFilter: (f: WireFilter) => void;
  /** Block-diagram view toggle: when ON, multiple harness-edges between the
   *  same pair of blocks collapse into a single line with an aggregate
   *  label ("2 harnesses" / "MOTOR_HARN · HALL_HARN"). Leaves the
   *  underlying data untouched — just a render-time view. */
  blockDiagramSimplified: boolean;
  toggleBlockDiagramSimplified: () => void;
  snapToGrid: boolean;
  theme: 'light' | 'dark';
  setTheme: (t: 'light' | 'dark') => void;
  toggleTheme: () => void;
  lastPastePosition: { x: number; y: number } | null;
  setLastPastePosition: (position: { x: number; y: number }) => void;
  pendingTool: PendingTool;
  setPendingTool: (tool: PendingTool) => void;

  // Sheet management
  setActiveSheet: (id: string) => void;
  addSheet: (kind: SheetKind, name?: string) => void;
  renameSheet: (id: string, name: string) => void;
  deleteSheet: (id: string) => void;
  deleteSheets: (ids: string[]) => void;
  /** Replace the sheets array with a new order. IDs not present in `orderedIds`
   *  are appended at the end (defensive — should not happen in practice). */
  reorderSheets: (orderedIds: string[]) => void;
  /** Convenience: create a "subsystem" — a Requirements + Block + Schematic
   *  trio with the given prefix. Centralised here so the welcome banner,
   *  the project-tree modal, and the picker modal all use the same logic. */
  addSubsystem: (name: string) => void;

  // Canvas
  onNodesChange: (changes: NodeChange<AnyNode>[]) => void;
  onEdgesChange: (changes: EdgeChange<WireEdge>[]) => void;
  onConnect: (connection: Connection) => void;
  onReconnect: (oldEdge: WireEdge, newConnection: Connection) => void;

  // Node creation
  addComponent: (templateKey: string, position: { x: number; y: number }) => void;
  addComponentFromTemplate: (
    template: ComponentTemplate,
    position: { x: number; y: number },
  ) => void;
  syncTemplateInstances: (
    template: ComponentTemplate,
    previousTemplate?: ComponentTemplate,
  ) => { components: number; mechanicalItems: number; removedEdges: number };
  addAnnotation: (position: { x: number; y: number }, text?: string) => void;
  addImage: (
    position: { x: number; y: number },
    src: string,
    size?: { width?: number; height?: number },
    name?: string,
  ) => void;
  addBlock: (position: { x: number; y: number }, title?: string) => void;
  /** Create (or jump to) the project-level "System Overview" block sheet with
   *  one block per subsystem — draw harness lines between them there. */
  addSystemOverview: () => void;
  addMechanicalItem: (
    position: { x: number; y: number },
    item: MechanicalItemData,
  ) => void;
  addMeasurement: (
    start: { x: number; y: number },
    end: { x: number; y: number },
  ) => void;
  addPowerSymbol: (position: { x: number; y: number }, symbol: PowerSymbolKind, name?: string) => void;
  addNetLabel: (position: { x: number; y: number }, name?: string) => void;
  addSplice: (position: { x: number; y: number }) => void;
  convertEdgeToNetLabels: (edgeId: string, name: string) => void;

  // Data mutation
  setAnnotationText: (id: string, text: string) => void;
  setAnnotationField: (id: string, patch: Partial<AnnotationData>) => void;
  setImageField: (id: string, patch: Partial<ImageData>) => void;
  setBlockField: (id: string, patch: Partial<BlockData>) => void;
  /** Set the net name on a power symbol or net label node. */
  setNetNodeName: (id: string, name: string) => void;
  setMechanicalItemField: (id: string, patch: Partial<MechanicalItemData>) => void;
  setMechanicalSheetScale: (scalePxPerMm: number) => void;

  // Requirements CRUD (on the active sheet, if kind === 'requirements')
  addRequirement: (seed?: Partial<Requirement>) => void;
  updateRequirement: (id: string, patch: Partial<Requirement>) => void;
  removeRequirement: (id: string) => void;
  moveRequirement: (id: string, dir: -1 | 1) => void;

  // Interconnect bundle CRUD (on the active sheet, if kind === 'interconnect')
  addBundle: (seed?: Partial<InterconnectBundle>) => void;
  updateBundle: (id: string, patch: Partial<InterconnectBundle>) => void;
  removeBundle: (id: string) => void;
  moveBundle: (id: string, dir: -1 | 1) => void;

  // Document section CRUD (on the active sheet, if kind === 'document')
  addSection: (seed?: Partial<DocumentSection>) => void;
  updateSection: (id: string, patch: Partial<DocumentSection>) => void;
  removeSection: (id: string) => void;
  moveSection: (id: string, dir: -1 | 1) => void;
  setDocumentCategory: (category: string) => void;
  updateNodeData: (id: string, patch: Partial<ComponentData>) => void;
  updatePin: (nodeId: string, pinId: string, patch: Partial<Pin>) => void;
  addPin: (nodeId: string, pin: Omit<Pin, 'id'>) => void;
  removePin: (nodeId: string, pinId: string) => void;
  renameGroup: (nodeId: string, oldName: string, newName: string) => void;
  rotateNode: (nodeId: string, deltaDeg?: number) => void;
  updateEdgeData: (id: string, patch: Partial<WireData>) => void;
  /** Bulk-assign (or clear) the harness on every wire whose id is in the
   *  list. Operates on the active sheet only — multi-select is per-canvas. */
  assignHarnessToEdges: (edgeIds: string[], harnessId: string | undefined) => void;
  assignHarnessToEdgesAcrossSheets: (edgeIds: string[], harnessId: string | undefined) => void;
  createHarnessForEdges: (edgeIds: string[]) => string | null;
  normalizeHarnessNames: () => number;
  /** Patch wire data on specific edges across every sheet (connection list). */
  updateEdgesAcrossSheets: (edgeIds: string[], patch: Partial<WireData>) => void;
  /** Auto-assign sequential wire numbers (W1, W2…) to every schematic wire
   *  that doesn't already have one. Returns how many were numbered. */
  autoNumberWires: () => number;
  /** Navigate to a specific wire across the project: switch to the sheet
   *  it lives on, select it, and clear any other selection. Used by the
   *  Interconnect sheet's per-harness wire list. */
  goToEdge: (edgeId: string) => void;

  /** Apply a state snapshot received from another window (the multi-window
   *  pop-out feature uses this). Updates `meta` and `sheets` only; leaves
   *  per-window state like `activeSheetId`, `selectedNodeId`, focus etc.
   *  alone so each window can keep its own viewing context. */
  syncFromRemote: (meta: ProjectMeta, sheets: Sheet[]) => void;

  // Selection + deletion
  selectNode: (id: string | null) => void;
  selectEdge: (id: string | null) => void;
  /** Toggle (or set) the harness focus. Pass null to clear.
   *  When activating focus, automatically jumps to the first block/schematic
   *  sheet that has a wire assigned to this harness so the user immediately
   *  sees the highlighted run. */
  focusHarness: (id: string | null) => void;
  focusNet: (net: { id: string; edgeIds: string[]; nodeIds: string[] } | null) => void;
  setFocusedNetMode: (mode: 'dim' | 'solo') => void;
  autoAssignNetNamesFromWireLabels: () => {
    updated: number;
    groups: Array<{ name: string; count: number }>;
  };
  moveNodeLayer: (id: string, mode: 'front' | 'back' | 'forward' | 'backward') => void;
  deleteNode: (id: string) => void;
  deleteEdge: (id: string) => void;

  // Meta / project
  updateMeta: (patch: Partial<ProjectMeta>) => void;
  setProject: (state: {
    meta: ProjectMeta;
    sheets: Sheet[];
    activeSheetId: string;
  }) => void;
  newProject: () => void;

  // Clipboard / layout / UI toggles
  copySelectedNode: () => boolean;
  pasteClipboard: (position?: { x: number; y: number }) => boolean;
  hasClipboard: () => boolean;
  alignNodes: (mode: 'left' | 'right' | 'top' | 'bottom' | 'center-h' | 'center-v') => void;
  distributeNodes: (axis: 'h' | 'v') => void;
  toggleSnapToGrid: () => void;
}

let CLIPBOARD_GRAPH: { nodes: AnyNode[]; edges: WireEdge[] } | null = null;

function cloneNodeForPaste(
  src: AnyNode,
  newPosition: { x: number; y: number },
  nodeIdMap: Map<string, string>,
  pinIdMap: Map<string, Map<string, string>>,
): AnyNode {
  const nextId = nanoid(8);
  nodeIdMap.set(src.id, nextId);
  if (isCompNode(src)) {
    const perNodePins = new Map<string, string>();
    const nextPins = src.data.pins.map((p) => {
      const nextPinId = nanoid(6);
      perNodePins.set(p.id, nextPinId);
      return { ...p, id: nextPinId };
    });
    pinIdMap.set(src.id, perNodePins);
    return {
      ...src,
      id: nextId,
      position: newPosition,
      selected: true,
      dragging: false,
      data: {
        ...src.data,
        properties: { ...src.data.properties },
        pins: nextPins,
      },
    };
  }
  if (isAnnotationNode(src)) {
    return {
      ...src,
      id: nextId,
      position: newPosition,
      selected: true,
      dragging: false,
      data: { ...src.data },
    };
  }
  if (isImageNode(src)) {
    return {
      ...src,
      id: nextId,
      position: newPosition,
      selected: true,
      dragging: false,
      data: { ...src.data },
    };
  }
  if (isMechanicalItemNode(src)) {
    return {
      ...src,
      id: nextId,
      position: newPosition,
      selected: true,
      dragging: false,
      data: { ...src.data },
    };
  }
  if (isSpliceNode(src)) {
    return {
      ...src,
      id: nextId,
      position: newPosition,
      selected: true,
      dragging: false,
      data: { ...src.data },
    };
  }
  return {
    ...(src as BlockNode),
    id: nextId,
    position: newPosition,
    selected: true,
    dragging: false,
    data: { ...(src as BlockNode).data },
  };
}

function makePins(pins: Omit<Pin, 'id'>[]): Pin[] {
  return pins.map((p) => ({ ...p, id: nanoid(6) }));
}

function makeSyncedPins(existing: Pin[], templatePins: Omit<Pin, 'id'>[]): Pin[] {
  const existingByName = new Map<string, Pin[]>();
  for (const pin of existing) {
    const key = pin.name.trim().toLowerCase();
    const list = existingByName.get(key);
    if (list) list.push(pin);
    else existingByName.set(key, [pin]);
  }
  const usedIds = new Set<string>();
  return templatePins.map((pin) => {
    const key = pin.name.trim().toLowerCase();
    const match = (existingByName.get(key) ?? []).find((candidate) => !usedIds.has(candidate.id));
    const id = match?.id ?? nanoid(6);
    usedIds.add(id);
    return { ...pin, id };
  });
}

function normalizeMatchText(value: string | undefined) {
  return (value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function isMeaningfulPartNumber(value: string | undefined) {
  const normalized = normalizeMatchText(value);
  return Boolean(normalized && normalized !== '-' && normalized !== 'n/a' && normalized !== 'na' && normalized !== 'yok');
}

function pinSignature(pins: Array<Pick<Pin, 'name' | 'group'>>) {
  return pins
    .map((pin) => `${normalizeMatchText(pin.group)}:${normalizeMatchText(pin.name)}`)
    .sort()
    .join('|');
}

function labelsAreRelated(a: string | undefined, b: string | undefined) {
  const left = normalizeMatchText(a);
  const right = normalizeMatchText(b);
  if (!left || !right) return false;
  // Exact match only. Substring matching was too loose — editing the "Motor"
  // template would also rewrite "Motor Driver" instances and drop their wires.
  return left === right;
}

function matchesTemplateIdentity(
  data: Pick<ComponentData, 'templateKey' | 'label' | 'category' | 'partNumber' | 'pins'>,
  tpl: ComponentTemplate,
  previousTpl?: ComponentTemplate,
) {
  if (data.templateKey) return data.templateKey === tpl.key;

  const currentLabel = normalizeMatchText(data.label);
  const previousLabels = [previousTpl?.label, tpl.label].map(normalizeMatchText).filter(Boolean);
  if (previousLabels.includes(currentLabel)) return true;
  if (labelsAreRelated(data.label, previousTpl?.label) || labelsAreRelated(data.label, tpl.label)) return true;

  const dataPart = normalizeMatchText(data.partNumber);
  const templatePart = normalizeMatchText(previousTpl?.partNumber ?? tpl.partNumber);
  const sameMeaningfulPart =
    isMeaningfulPartNumber(data.partNumber) &&
    dataPart === templatePart &&
    normalizeMatchText(data.category) === normalizeMatchText(previousTpl?.category ?? tpl.category);
  if (!sameMeaningfulPart) return false;

  const templatePins = previousTpl?.pins ?? tpl.pins;
  return data.pins.length === templatePins.length && pinSignature(data.pins) === pinSignature(templatePins);
}

function matchesMechanicalTemplateIdentity(
  data: Pick<MechanicalItemData, 'templateKey' | 'label' | 'partNumber'>,
  tpl: ComponentTemplate,
  previousTpl?: ComponentTemplate,
) {
  if (data.templateKey) return data.templateKey === tpl.key;

  const label = normalizeMatchText(data.label);
  const previousLabels = [previousTpl?.label, tpl.label].map(normalizeMatchText).filter(Boolean);
  if (previousLabels.includes(label)) return true;
  if (labelsAreRelated(data.label, previousTpl?.label) || labelsAreRelated(data.label, tpl.label)) return true;

  return (
    isMeaningfulPartNumber(data.partNumber) &&
    normalizeMatchText(data.partNumber) === normalizeMatchText(previousTpl?.partNumber ?? tpl.partNumber)
  );
}

function normalizeAutoWireNetName(label: string | undefined): string | null {
  const raw = (label ?? '').trim().toUpperCase().replace(/\s+/g, ' ');
  if (!raw) return null;
  if (/^\+?24V$/.test(raw) || raw === 'DC+' || raw === 'V+') return '+24V';
  if (/^24V\s*(RTN|RETURN|RET|0V|-)$/.test(raw) || raw === '0V' || raw === 'DC-' || raw === '-V') {
    return '0V';
  }
  if (raw === 'GND' || raw === 'GROUND') return 'GND';
  if (raw === 'PE' || raw === 'EARTH' || raw === 'CHASSIS' || raw === 'ŞASE' || raw === 'SASE') return 'PE';
  if (/^\+?12V$/.test(raw)) return '+12V';
  if (/^12V\s*(RTN|RETURN|RET|0V|-)$/.test(raw)) return '12V_RTN';
  if (/^\+?5V$/.test(raw)) return '+5V';
  if (raw === 'L' || raw === 'LINE') return 'L';
  if (raw === 'N' || raw === 'NEUTRAL') return 'N';
  return null;
}

function subsystemPrefixFromSheetName(name: string | undefined): string {
  const base = (name ?? 'PROJECT')
    .replace(/\s+[—-]\s+(requirements|block diagram|block|schematic|mechanical|interconnect|document)$/i, '')
    .replace(/\s+(requirements|block diagram|block|schematic|mechanical|interconnect|document)$/i, '')
    .trim();
  const ascii = base
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ı/g, 'i')
    .replace(/İ/g, 'I')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '');
  return ascii || 'PROJECT';
}

function endpointDisplayName(sheet: Sheet, nodeId: string | null | undefined): string {
  if (!nodeId) return '?';
  const node = sheet.nodes.find((n) => n.id === nodeId);
  if (!node) return '?';
  if (isCompNode(node)) return node.data.label || node.id;
  if (isBlockNode(node)) return node.data.title || node.id;
  if (isSpliceNode(node)) return node.data.label || node.id;
  if (isPowerSymbolNode(node) || isNetLabelNode(node)) return node.data.name || node.id;
  const data = node.data as { label?: unknown; title?: unknown; name?: unknown };
  const label = data.label ?? data.title ?? data.name;
  return typeof label === 'string' && label.trim() ? label.trim() : node.id;
}

function edgeSubsystemPrefix(sheet: Sheet): string {
  return subsystemPrefixFromSheetName(sheet.name);
}

function makeHarnessName(prefix: string, seq: number): string {
  return `${prefix}-HARN-${String(seq).padStart(3, '0')}`;
}

function nextHarnessName(prefix: string, used: Set<string>): string {
  let seq = 1;
  let name = makeHarnessName(prefix, seq);
  while (used.has(name)) {
    seq += 1;
    name = makeHarnessName(prefix, seq);
  }
  used.add(name);
  return name;
}

function inferHarnessPrefixForEdges(sheets: Sheet[], edgeIds: string[]): string {
  const wanted = new Set(edgeIds);
  const prefixes = new Set<string>();
  for (const sh of sheets) {
    if (sh.kind !== 'schematic') continue;
    if (sh.edges.some((e) => wanted.has(e.id))) prefixes.add(edgeSubsystemPrefix(sh));
  }
  if (prefixes.size === 1) return [...prefixes][0];
  return 'PROJECT';
}

function harnessSortKey(sheets: Sheet[], harnessId: string): string {
  const parts: string[] = [];
  for (const sh of sheets) {
    if (sh.kind !== 'schematic' && sh.kind !== 'block') continue;
    for (const edge of sh.edges) {
      if (edge.data?.harnessId !== harnessId) continue;
      const a = endpointDisplayName(sh, edge.source);
      const b = endpointDisplayName(sh, edge.target);
      parts.push(a < b ? `${a}->${b}` : `${b}->${a}`);
    }
  }
  return parts.sort().join('|');
}

const PIN_PITCH = 16;
const GROUP_GAP = 18;
const GROUP_HEADER = 14;
const SIDE_PADDING = 14;
const BRACKET_AREA = 28;

const SIDE_ORDER: PinSide[] = ['top', 'right', 'bottom', 'left'];

function rotatePinSide(side: PinSide, rotation: number): PinSide {
  const idx = SIDE_ORDER.indexOf(side);
  const steps = Math.round(((rotation % 360) + 360) % 360 / 90);
  return SIDE_ORDER[(idx + steps) % 4];
}

function layoutPinsForSide(pins: Pin[]) {
  let cursor = SIDE_PADDING;
  const out = new Map<string, number>();
  let lastGroup: string | null = null;
  pins.forEach((pin, index) => {
    const group = (pin.group ?? '').trim();
    if (index > 0 && group !== lastGroup) cursor += GROUP_GAP;
    if (group && group !== lastGroup) cursor += GROUP_HEADER;
    out.set(pin.id, cursor);
    cursor += PIN_PITCH;
    lastGroup = group;
  });
  return { positions: out, length: cursor + SIDE_PADDING };
}

function getPinNetLabelPlacement(node: CompNode, handleId: string | null | undefined) {
  const pin = node.data.pins.find((p) => p.id === handleId);
  if (!pin) {
    return {
      position: { x: node.position.x + 28, y: node.position.y - 18 },
      side: 'left' as PinSide,
    };
  }
  const rotation = node.data.rotation ?? 0;
  const effectiveSide = rotatePinSide(pin.side, rotation);
  const buckets: Record<PinSide, Pin[]> = { top: [], right: [], bottom: [], left: [] };
  for (const p of node.data.pins) buckets[rotatePinSide(p.side, rotation)].push(p);
  const left = layoutPinsForSide(buckets.left);
  const right = layoutPinsForSide(buckets.right);
  const top = layoutPinsForSide(buckets.top);
  const bottom = layoutPinsForSide(buckets.bottom);
  const hasTopGroups = buckets.top.some((p) => (p.group ?? '').trim());
  const hasBottomGroups = buckets.bottom.some((p) => (p.group ?? '').trim());
  const hasLeftGroups = buckets.left.some((p) => (p.group ?? '').trim());
  const hasRightGroups = buckets.right.some((p) => (p.group ?? '').trim());
  const topBracketH = hasTopGroups ? BRACKET_AREA : 0;
  const leftBracketW = hasLeftGroups ? BRACKET_AREA : 0;
  const rightBracketW = hasRightGroups ? BRACKET_AREA : 0;
  const headerH = 38;
  const innerW = Math.max(180, Math.max(top.length, bottom.length));
  const innerH = Math.max(60, Math.max(left.length, right.length));
  const width = leftBracketW + innerW + rightBracketW;
  const height = topBracketH + headerH + innerH + (hasBottomGroups ? BRACKET_AREA : 0);
  const margin = 18;
  if (effectiveSide === 'left') {
    return {
      position: {
        x: node.position.x - margin - 20,
        y: node.position.y + topBracketH + headerH + (left.positions.get(pin.id) ?? SIDE_PADDING) - 8,
      },
      side: 'right' as PinSide,
    };
  }
  if (effectiveSide === 'right') {
    return {
      position: {
        x: node.position.x + width + margin,
        y: node.position.y + topBracketH + headerH + (right.positions.get(pin.id) ?? SIDE_PADDING) - 8,
      },
      side: 'left' as PinSide,
    };
  }
  if (effectiveSide === 'top') {
    return {
      position: {
        x: node.position.x + leftBracketW + (top.positions.get(pin.id) ?? SIDE_PADDING) - 8,
        y: node.position.y - margin - 16,
      },
      side: 'bottom' as PinSide,
    };
  }
  return {
    position: {
      x: node.position.x + leftBracketW + (bottom.positions.get(pin.id) ?? SIDE_PADDING) - 8,
      y: node.position.y + height + margin,
    },
    side: 'top' as PinSide,
  };
}

function makeNetLabelConversion(
  edge: WireEdge,
  source: CompNode,
  target: CompNode,
  cleanName: string,
) {
  const sourcePlacement = getPinNetLabelPlacement(source, edge.sourceHandle);
  const targetPlacement = getPinNetLabelPlacement(target, edge.targetHandle);
  const labelA: NetLabelNode = {
    id: nanoid(8),
    type: 'netLabel',
    position: sourcePlacement.position,
    data: { name: cleanName, side: sourcePlacement.side },
    selected: true,
  };
  const labelB: NetLabelNode = {
    id: nanoid(8),
    type: 'netLabel',
    position: targetPlacement.position,
    data: { name: cleanName, side: targetPlacement.side },
    selected: true,
  };
  const sharedData = {
    ...(edge.data ?? {}),
    label: '',
    netName: cleanName,
    midX: undefined,
    midY: undefined,
    midX2: undefined,
    midY2: undefined,
  };
  const linkA: WireEdge = {
    id: nanoid(8),
    type: 'adjustable',
    source: edge.source,
    sourceHandle: edge.sourceHandle,
    target: labelA.id,
    targetHandle: NET_TERMINAL_HANDLE,
    data: sharedData,
    label: '',
  };
  const linkB: WireEdge = {
    id: nanoid(8),
    type: 'adjustable',
    source: labelB.id,
    sourceHandle: NET_TERMINAL_HANDLE,
    target: edge.target,
    targetHandle: edge.targetHandle,
    data: sharedData,
    label: '',
  };
  return { labels: [labelA, labelB], links: [linkA, linkB] };
}

const emptyMeta = (): ProjectMeta => ({
  name: 'Untitled Project',
  description: '',
  author: '',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

/**
 * A brand-new project starts empty so the user can name their own sheets /
 * subsystems instead of inheriting auto-created defaults like "Requirements"
 * and "Block Diagram". The welcome banner walks them through adding their
 * first subsystem; from there the project tree's `+` button covers the rest.
 */
function defaultSheets(): Sheet[] {
  return [];
}

/**
 * Normalise loaded project data so missing fields can't crash the renderer:
 *
 *   • Every sheet gets non-null `nodes` and `edges` arrays (older saves
 *     occasionally have them omitted).
 *   • Each component node has its `pins` / `properties` populated (we hit a
 *     black-screen crash once when a component had `data.pins === undefined`).
 *
 * Note (changed in v0.4.27): this used to also auto-create canonical sheets
 * (Requirements / Block Diagram / Schematic / Interconnect) for any project
 * that was missing them. That behaviour was removed because users want to
 * name their own sheets — the welcome banner now invites them to create a
 * subsystem instead.
 */
export function ensureSheetsCoverage(sheets: Sheet[]): Sheet[] {
  // First pass — patch each sheet's required arrays.
  const safe: Sheet[] = sheets
    .filter((s) => s && typeof s === 'object')
    .map((s) => {
      const cleanedNodes = Array.isArray(s.nodes)
        ? s.nodes.map((n) => {
            // Component nodes must always have pins/properties; drawing relies on them.
            if (n && (n as { type?: string }).type === 'component') {
              const data = (n.data ?? {}) as Partial<ComponentData>;
              return {
                ...n,
                data: {
                  label: data.label ?? '',
                  templateKey: data.templateKey,
                  category: data.category ?? '',
                  partNumber: data.partNumber,
                  manufacturer: data.manufacturer,
                  properties: data.properties ?? {},
                  pins: Array.isArray(data.pins) ? data.pins : [],
                  color: data.color,
                  rotation: data.rotation,
                },
              } as AnyNode;
            }
            if (n && (n as { type?: string }).type === 'block') {
              const data = (n.data ?? {}) as Partial<BlockData>;
              return {
                ...n,
                data: {
                  title: data.title ?? '',
                  subtitle: data.subtitle,
                  color: data.color ?? '#1e40af',
                  textColor: data.textColor ?? '#ffffff',
                  shape: data.shape ?? 'rounded',
                  width: data.width ?? 200,
                  height: data.height ?? 96,
                },
              } as AnyNode;
            }
            if (n && (n as { type?: string }).type === 'image') {
              const data = (n.data ?? {}) as Partial<ImageData>;
              return {
                ...n,
                data: {
                  src: data.src ?? '',
                  name: data.name,
                  width: data.width ?? 260,
                  height: data.height,
                },
              } as AnyNode;
            }
            if (n && (n as { type?: string }).type === 'mechanicalItem') {
              const data = (n.data ?? {}) as Partial<MechanicalItemData>;
              return {
                ...n,
                data: {
                  label: data.label ?? 'Mechanical item',
                  templateKey: data.templateKey,
                  partNumber: data.partNumber,
                  manufacturer: data.manufacturer,
                  color: data.color ?? '#64748b',
                  widthMm: data.widthMm ?? 100,
                  heightMm: data.heightMm ?? 60,
                  depthMm: data.depthMm,
                  massKg: data.massKg,
                  mounting: data.mounting ?? 'free',
                  orientation: data.orientation ?? 'top',
                  clearanceMm: data.clearanceMm,
                  notes: data.notes,
                  scalePxPerMm: data.scalePxPerMm ?? 1,
                  labelOffsetX: data.labelOffsetX,
                  labelOffsetY: data.labelOffsetY,
                  rotation: data.rotation ?? 0,
                },
              } as AnyNode;
            }
            if (n && (n as { type?: string }).type === 'measurement') {
              const data = (n.data ?? {}) as Partial<MeasurementData>;
              return {
                ...n,
                data: {
                  width: data.width ?? 120,
                  height: data.height ?? 0,
                  scalePxPerMm: data.scalePxPerMm ?? 1,
                  antiDiagonal: data.antiDiagonal,
                  label: data.label,
                },
              } as AnyNode;
            }
            if (n && (n as { type?: string }).type === 'splice') {
              const data = (n.data ?? {}) as Partial<SpliceData>;
              return {
                ...n,
                data: {
                  label: data.label ?? 'J1',
                  kind: data.kind ?? 'splice',
                  notes: data.notes ?? '',
                },
              } as AnyNode;
            }
            return n;
          })
        : [];
      return {
        ...s,
        nodes: cleanedNodes,
        edges: Array.isArray(s.edges) ? s.edges : [],
      } as Sheet;
    });
  return safe;
}

/** Helper that mutates only the active sheet. */
/** Sheet-name separator used to group sheets into subsystem folders — matches
 *  em dash, en dash or hyphen (mirrors the parser in ProjectTree). */
const SUBSYSTEM_SEPARATOR = /^(.+?)\s*[—–-]\s*(.+)$/;

/** Distinct subsystem folder names in the project (insertion order). */
export function listSubsystemNames(sheets: Sheet[]): string[] {
  const set = new Set<string>();
  for (const sh of sheets) {
    const m = sh.name.match(SUBSYSTEM_SEPARATOR);
    if (m) set.add(m[1].trim());
  }
  return [...set];
}

/** Number of distinct subsystem folders in the project. */
export function countSubsystems(sheets: Sheet[]): number {
  return listSubsystemNames(sheets).length;
}

/** True when the project has more subsystems than the free plan allows. */
export function projectExceedsFreeLimits(sheets: Sheet[]): boolean {
  return countSubsystems(sheets) > FREE_LIMITS.subsystems;
}

/** A free account looking at an over-limit project is read-only: edits are
 *  blocked (but deleting sheets/subsystems stays allowed so they can shrink the
 *  project back under the free limit and regain editing without upgrading). */
function editLocked(state: StoreState): boolean {
  return !isPro() && projectExceedsFreeLimits(state.sheets);
}

/** React hook mirroring editLocked, for UI (banner, disabled affordances). */
export function useReadOnly(): boolean {
  const pro = useIsPro();
  const sheets = useStore((s) => s.sheets);
  return useMemo(() => !pro && projectExceedsFreeLimits(sheets), [pro, sheets]);
}

function makeOverviewBlock(title: string, i: number): BlockNode {
  return {
    id: nanoid(8),
    type: 'block',
    position: { x: 120 + (i % 2) * 420, y: 120 + Math.floor(i / 2) * 220 },
    data: {
      title,
      color: '#1e40af',
      textColor: '#ffffff',
      shape: 'rounded',
      width: 220,
      height: 110,
    },
  };
}

/**
 * Create the project-level "System Overview" block sheet (one block per
 * subsystem, ready for harness lines between them) — or, when it already
 * exists, append blocks for subsystems that don't have one yet, so the
 * overview keeps up as the project grows. Mutations respect the read-only
 * gate; jumping to the sheet (`activate`) is plain navigation and always
 * allowed.
 */
function upsertSystemOverview(
  get: () => StoreState,
  set: (partial: Partial<StoreState>) => void,
  activate: boolean,
) {
  const locked = editLocked(get());
  const subs = listSubsystemNames(get().sheets);
  const existing = get().sheets.find(
    (sh) => sh.kind === 'block' && sh.name === 'System Overview',
  );
  if (existing) {
    if (!locked) {
      const have = new Set(
        existing.nodes.map((n) => (n.data as { title?: string })?.title).filter(Boolean),
      );
      const missing = subs.filter((s) => !have.has(s));
      if (missing.length > 0) {
        const base = existing.nodes.length;
        const added = missing.map((name, i) => makeOverviewBlock(name, base + i));
        set({
          sheets: get().sheets.map((sh) =>
            sh.id === existing.id ? { ...sh, nodes: [...sh.nodes, ...added] } : sh,
          ),
        });
      }
    }
    if (activate) set({ activeSheetId: existing.id });
    return;
  }
  if (locked) return;
  const sheet: Sheet = {
    id: nanoid(8),
    name: 'System Overview',
    kind: 'block',
    nodes: subs.map((name, i) => makeOverviewBlock(name, i)),
    edges: [],
  };
  set({
    sheets: [sheet, ...get().sheets],
    ...(activate ? { activeSheetId: sheet.id } : {}),
  });
}

function makeKitSheet(kind: 'interconnect' | 'schematic', name: string): Sheet {
  return {
    id: nanoid(8),
    name,
    kind,
    nodes: [],
    edges: [],
    ...(kind === 'interconnect' ? { bundles: [] } : {}),
  };
}

/**
 * Pure backfill for the between-subsystems kit, run when a project loads
 * (setProject). Projects that predate the feature — or were made in another
 * app version — get the System Overview + Harnesses + System Wiring sheets the
 * first time they open with ≥2 subsystems. A one-time meta flag records that
 * it ran, so nothing resurrects after the user deletes a kit sheet, and a
 * read-only (over-limit free) project is left untouched (and unflagged, so it
 * provisions once the user upgrades). Only ADDS what's missing — never edits
 * existing sheets.
 */
function ensureSystemKit(
  sheets: Sheet[],
  meta: ProjectMeta,
): { sheets: Sheet[]; meta: ProjectMeta } {
  if (meta.systemKitProvisioned) return { sheets, meta };
  if (!isPro() && projectExceedsFreeLimits(sheets)) return { sheets, meta };
  const subs = listSubsystemNames(sheets);
  if (subs.length < 2) return { sheets, meta };

  let next = sheets;
  if (!next.some((sh) => sh.kind === 'block' && sh.name === 'System Overview')) {
    const overview: Sheet = {
      id: nanoid(8),
      name: 'System Overview',
      kind: 'block',
      nodes: subs.map((name, i) => makeOverviewBlock(name, i)),
      edges: [],
    };
    next = [overview, ...next];
  }
  if (!next.some((sh) => sh.kind === 'interconnect')) {
    next = [...next, makeKitSheet('interconnect', 'Harnesses')];
  }
  if (!next.some((sh) => sh.kind === 'schematic' && !SUBSYSTEM_SEPARATOR.test(sh.name))) {
    next = [...next, makeKitSheet('schematic', 'System Wiring')];
  }
  return { sheets: next, meta: { ...meta, systemKitProvisioned: true } };
}

function mutateSheet(
  get: () => StoreState,
  set: (partial: Partial<StoreState>) => void,
  fn: (sheet: Sheet) => Sheet,
) {
  if (editLocked(get())) return;
  const id = get().activeSheetId;
  set({
    sheets: get().sheets.map((sh) => (sh.id === id ? fn(sh) : sh)),
  });
}

function withLayerIndexes(nodes: AnyNode[]): AnyNode[] {
  return nodes.map((node, index) => ({ ...node, zIndex: index }));
}

function shiftInternalEdgeBendsOnNodeMove(
  oldNodes: AnyNode[],
  edges: WireEdge[],
  changes: NodeChange<AnyNode>[],
): WireEdge[] {
  const byId = new Map(oldNodes.map((n) => [n.id, n]));
  const deltas = new Map<string, { dx: number; dy: number }>();

  for (const change of changes) {
    if (change.type !== 'position' || !change.position) continue;
    const oldNode = byId.get(change.id);
    if (!oldNode) continue;
    const dx = change.position.x - oldNode.position.x;
    const dy = change.position.y - oldNode.position.y;
    if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) continue;
    deltas.set(change.id, { dx, dy });
  }

  if (deltas.size === 0) return edges;

  let changed = false;
  const next = edges.map((edge) => {
    const sourceDelta = deltas.get(edge.source);
    const targetDelta = deltas.get(edge.target);
    if (!sourceDelta || !targetDelta) return edge;

    // Only shift preserved manual bends when the whole connection moved as a
    // group. If one endpoint moved independently, keep the bend data anchored
    // so the user can reshape that one connection deliberately.
    if (
      Math.abs(sourceDelta.dx - targetDelta.dx) > 0.001 ||
      Math.abs(sourceDelta.dy - targetDelta.dy) > 0.001
    ) {
      return edge;
    }

    const data = edge.data ?? {};
    const hasBend =
      typeof data.midX === 'number' ||
      typeof data.midY === 'number' ||
      typeof data.midX2 === 'number' ||
      typeof data.midY2 === 'number';
    if (!hasBend) return edge;

    changed = true;
    return {
      ...edge,
      data: {
        ...data,
        midX: typeof data.midX === 'number' ? data.midX + sourceDelta.dx : data.midX,
        midY: typeof data.midY === 'number' ? data.midY + sourceDelta.dy : data.midY,
        midX2: typeof data.midX2 === 'number' ? data.midX2 + sourceDelta.dx : data.midX2,
        midY2: typeof data.midY2 === 'number' ? data.midY2 + sourceDelta.dy : data.midY2,
      },
    };
  });

  return changed ? next : edges;
}

/** Stable sentinel returned when the project has no sheets at all (a brand-
 *  new project starts this way). Selectors return its empty arrays so the
 *  rest of the app can render without crashes; the canvas / project tree
 *  detect it via `id === '__empty__'` and render an onboarding view. */
const EMPTY_SHEET: Sheet = {
  id: '__empty__',
  name: '',
  kind: 'schematic',
  nodes: [],
  edges: [],
};

/** True iff the project has no sheets at all (brand-new state). */
export function hasNoSheets(s: StoreState): boolean {
  return s.sheets.length === 0;
}

/** Reads the active sheet for selectors. Falls back to the EMPTY_SHEET
 *  sentinel when the project is empty. */
export function activeSheet(s: StoreState): Sheet {
  if (s.sheets.length === 0) return EMPTY_SHEET;
  return s.sheets.find((sh) => sh.id === s.activeSheetId) ?? s.sheets[0];
}

// Convenience selectors
export const selectActiveNodes = (s: StoreState): AnyNode[] => activeSheet(s).nodes;
export const selectActiveEdges = (s: StoreState): WireEdge[] => activeSheet(s).edges;

/**
 * All cable bundles defined in any interconnect sheet across the project.
 *
 * IMPORTANT: do NOT call this with `useStore(selectAllBundles)`. The function
 * allocates a fresh array every invocation, and React 19's
 * `useSyncExternalStore` (which Zustand uses under the hood) refuses to cache
 * an unstable snapshot — it throws "Maximum update depth exceeded" (#185)
 * the moment any component renders.
 *
 * Instead, subscribe to the stable `s.sheets` reference and derive the
 * bundle list inside a `useMemo`:
 *
 *     const sheets = useStore((s) => s.sheets);
 *     const bundles = useMemo(
 *       () => sheets.flatMap((sh) => sh.bundles ?? []),
 *       [sheets],
 *     );
 *
 * This selector is still safe to use with `useStore.getState()` for one-off
 * reads (no subscription).
 */
export const selectAllBundles = (s: StoreState): InterconnectBundle[] => {
  const out: InterconnectBundle[] = [];
  for (const sh of s.sheets) {
    if (sh.kind === 'interconnect' && sh.bundles) out.push(...sh.bundles);
  }
  return out;
};

/**
 * Count of wires (across every block/schematic sheet) currently assigned to
 * each harness. Returns a `Map<bundleId, count>`.
 *
 * Same caveat as `selectAllBundles`: this returns a fresh `Map` per call, so
 * it is NOT safe inside `useStore(...)`. Derive it via `useMemo` from a
 * `useStore((s) => s.sheets)` subscription instead.
 */
export const selectWireCountsByHarness = (s: StoreState): Map<string, number> => {
  const out = new Map<string, number>();
  for (const sh of s.sheets) {
    if (sh.kind !== 'block' && sh.kind !== 'schematic') continue;
    for (const e of sh.edges) {
      const hid = e.data?.harnessId;
      if (!hid) continue;
      out.set(hid, (out.get(hid) ?? 0) + 1);
    }
  }
  return out;
};
export const selectActiveSheet = (s: StoreState): Sheet => activeSheet(s);

/** Debounce zundo history writes so drags collapse into one undo entry.
 *  Keep the first pre-change snapshot, not the latest intermediate one. */
function debounceFirstHandleSet<T>(fn: (state: T) => void, ms = 700): (state: T) => void {
  let first: T | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (state: T) => {
    if (first == null) first = state;
    if (timer != null) clearTimeout(timer);
    timer = setTimeout(() => {
      if (first != null) fn(first);
      timer = null;
      first = null;
    }, ms);
  };
}

const initialSheets = defaultSheets();

export const useStore = create<StoreState>()(
  temporal(
    (set, get) => ({
      meta: emptyMeta(),
      sheets: initialSheets,
      activeSheetId: initialSheets[0]?.id ?? '',
      selectedNodeId: null,
      selectedEdgeId: null,
      focusedHarnessId: null,
      focusedNet: null,
      focusedNetMode: 'dim',
      wireFilter: 'all',
      blockDiagramSimplified: (() => {
        try {
          return localStorage.getItem('skema:blockSimplified') === '1';
        } catch {
          return false;
        }
      })(),
      snapToGrid: (() => {
        try {
          return localStorage.getItem('skema:snap') === '1';
        } catch {
          return false;
        }
      })(),
      lastPastePosition: null,
      setLastPastePosition: (position) => set({ lastPastePosition: position }),
      pendingTool: null,
      setPendingTool: (tool) => set({ pendingTool: tool }),

      // Single dark workstation theme — there is no light UI mode. These are
      // kept (as no-ops that always resolve to 'dark') so existing callers and
      // persisted state stay valid.
      theme: 'dark',
      setTheme: () => set({ theme: 'dark' }),
      toggleTheme: () => set({ theme: 'dark' }),
      toggleSnapToGrid: () => {
        const next = !get().snapToGrid;
        set({ snapToGrid: next });
        try {
          localStorage.setItem('skema:snap', next ? '1' : '0');
        } catch {
          // ignore
        }
      },

      toggleBlockDiagramSimplified: () => {
        const next = !get().blockDiagramSimplified;
        set({ blockDiagramSimplified: next });
        try {
          localStorage.setItem('skema:blockSimplified', next ? '1' : '0');
        } catch {
          // ignore
        }
      },

      setActiveSheet: (id) =>
        set({ activeSheetId: id, selectedNodeId: null, selectedEdgeId: null }),

      addSheet: (kind, name) => {
        if (editLocked(get())) return;
        const defaultName =
          kind === 'block'
            ? 'Block Diagram'
            : kind === 'mechanical'
              ? 'Mechanical Layout'
            : kind === 'requirements'
              ? 'Requirements'
              : kind === 'interconnect'
                ? 'Harnesses'
                : kind === 'document'
                  ? 'Document'
                  : 'Schematic';
        const sheet: Sheet = {
          id: nanoid(8),
          name: name ?? defaultName,
          kind,
          nodes: [],
          edges: [],
          ...(kind === 'requirements' ? { requirements: [] } : {}),
          ...(kind === 'interconnect' ? { bundles: [] } : {}),
          ...(kind === 'document' ? { sections: [], documentCategory: '' } : {}),
        };
        set({ sheets: [...get().sheets, sheet], activeSheetId: sheet.id });
      },

      renameSheet: (id, name) => {
        if (editLocked(get())) return;
        set({
          sheets: get().sheets.map((sh) => (sh.id === id ? { ...sh, name } : sh)),
        });
      },

      deleteSheet: (id) => {
        const current = get().sheets;
        const target = current.find((sh) => sh.id === id);
        if (!target) return;
        // Allow deleting any sheet — even the last one. The project can sit
        // empty (just like a fresh project) and the welcome banner / empty-
        // state UI will guide the user to add a new sheet.
        const sheets = current.filter((sh) => sh.id !== id);
        const activeSheetId =
          get().activeSheetId === id
            ? sheets[0]?.id ?? ''
            : get().activeSheetId;
        set({ sheets, activeSheetId, selectedNodeId: null, selectedEdgeId: null });
      },

      // Bulk delete (used to remove a whole subsystem — all its sheets at once).
      // Like deleteSheet, deleting everything is allowed; the empty-state UI
      // guides the user to add a sheet again.
      deleteSheets: (ids) => {
        const idSet = new Set(ids);
        const current = get().sheets;
        const remaining = current.filter((sh) => !idSet.has(sh.id));
        if (remaining.length === current.length) return;
        const activeSheetId = idSet.has(get().activeSheetId)
          ? remaining[0]?.id ?? ''
          : get().activeSheetId;
        set({ sheets: remaining, activeSheetId, selectedNodeId: null, selectedEdgeId: null });
      },

      reorderSheets: (orderedIds) => {
        if (editLocked(get())) return;
        const current = get().sheets;
        const lookup = new Map(current.map((sh) => [sh.id, sh]));
        const next: Sheet[] = [];
        for (const id of orderedIds) {
          const sh = lookup.get(id);
          if (sh) {
            next.push(sh);
            lookup.delete(id);
          }
        }
        // Defensive — append any sheets the caller forgot.
        for (const sh of lookup.values()) next.push(sh);
        set({ sheets: next });
      },

      addSubsystem: (name) => {
        const trimmed = name.trim();
        if (!trimmed) return;
        // Free plan is capped at FREE_LIMITS.subsystems. Count existing
        // subsystems by their distinct "<name> — " sheet prefixes (addSubsystem
        // is the only thing that creates them, so this stays accurate).
        if (!isPro()) {
          const groups = new Set<string>();
          for (const sh of get().sheets) {
            const i = sh.name.indexOf(' — ');
            if (i > 0) groups.add(sh.name.slice(0, i));
          }
          if (groups.size >= FREE_LIMITS.subsystems) {
            showProNotice(
              `Free plan is limited to ${FREE_LIMITS.subsystems} subsystem. Upgrade to Pro for unlimited subsystems.`,
            );
            return;
          }
        }
        // Three sheets prefixed with the subsystem name — the parser in
        // ProjectTree groups them under one folder.
        get().addSheet('requirements', `${trimmed} — Requirements`);
        get().addSheet('block', `${trimmed} — Block`);
        get().addSheet('schematic', `${trimmed} — Schematic`);
        get().addSheet('mechanical', `${trimmed} — Mechanical`);
        // Auto-provision the between-subsystems kit. From the FIRST subsystem a
        // System Overview (one block per subsystem, kept in sync as more are
        // added) and the project Harnesses sheet already make sense; a
        // project-wide "System Wiring" schematic only becomes meaningful once
        // there are two or more subsystems to wire together. Focus stays on the
        // subsystem just created (addSheet activates as it goes).
        const subCount = listSubsystemNames(get().sheets).length;
        const focus = get().activeSheetId;
        if (subCount >= 1) {
          upsertSystemOverview(get, set, false);
          if (!get().sheets.some((sh) => sh.kind === 'interconnect')) {
            get().addSheet('interconnect', 'Harnesses');
          }
        }
        if (subCount >= 2) {
          if (
            !get().sheets.some(
              (sh) => sh.kind === 'schematic' && !SUBSYSTEM_SEPARATOR.test(sh.name),
            )
          ) {
            get().addSheet('schematic', 'System Wiring');
          }
        }
        // Mark provisioned so the load-time backfill never re-adds these and
        // deleting one of them sticks.
        set({ activeSheetId: focus, meta: { ...get().meta, systemKitProvisioned: true } });
      },

      onNodesChange: (changes) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          nodes: applyNodeChanges(changes, sh.nodes),
          edges: shiftInternalEdgeBendsOnNodeMove(sh.nodes, sh.edges, changes),
        }));
      },

      onEdgesChange: (changes) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          edges: applyEdgeChanges(changes, sh.edges),
        }));
      },

      onConnect: (connection) => {
        const newEdge: WireEdge = {
          ...connection,
          id: nanoid(8),
          type: 'adjustable',
          data: { label: '', gauge: '', color: '', notes: '' },
          label: '',
        };
        mutateSheet(get, set, (sh) => ({ ...sh, edges: addEdge(newEdge, sh.edges) }));
      },

      onReconnect: (oldEdge, newConnection) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          edges: reconnectEdge(
            oldEdge,
            newConnection,
            sh.edges.map((e) =>
              e.id === oldEdge.id
                ? {
                    ...e,
                    data: {
                      ...(e.data ?? {}),
                      midX: undefined,
                      midY: undefined,
                      midX2: undefined,
                      midY2: undefined,
                    },
                  }
                : e,
            ),
          ),
        }));
      },

      addComponent: (templateKey, position) => {
        const tpl = getTemplate(templateKey);
        if (!tpl) return;
        get().addComponentFromTemplate(tpl, position);
      },

      addComponentFromTemplate: (tpl, position) => {
        const node: CompNode = {
          id: nanoid(8),
          type: 'component',
          position,
          data: {
            templateKey: tpl.key,
            label: tpl.label,
            category: tpl.category,
            partNumber: tpl.partNumber,
            manufacturer: tpl.manufacturer,
            properties: { ...tpl.defaultProperties },
            pins: makePins(tpl.pins),
            color: tpl.color,
          },
        };
        mutateSheet(get, set, (sh) => ({ ...sh, nodes: [...sh.nodes, node] }));
        set({ selectedNodeId: node.id });
      },

      syncTemplateInstances: (tpl, previousTpl) => {
        if (editLocked(get())) return { components: 0, mechanicalItems: 0, removedEdges: 0 };
        let components = 0;
        let mechanicalItems = 0;
        let removedEdges = 0;
        const sheets = get().sheets.map((sh) => {
          let changed = false;
          let allowedHandlesByNode: Map<string, Set<string>> | null = null;
          const nodes = sh.nodes.map((n) => {
            if (isCompNode(n) && matchesTemplateIdentity(n.data, tpl, previousTpl)) {
              components += 1;
              changed = true;
              const pins = makeSyncedPins(n.data.pins, tpl.pins);
              if (!allowedHandlesByNode) allowedHandlesByNode = new Map();
              allowedHandlesByNode.set(n.id, new Set(pins.map((p) => p.id)));
              return {
                ...n,
                data: {
                  ...n.data,
                  templateKey: tpl.key,
                  label: tpl.label,
                  category: tpl.category,
                  partNumber: tpl.partNumber,
                  manufacturer: tpl.manufacturer,
                  properties: { ...tpl.defaultProperties },
                  pins,
                  color: tpl.color,
                },
              };
            }
            if (isMechanicalItemNode(n) && matchesMechanicalTemplateIdentity(n.data, tpl, previousTpl)) {
              mechanicalItems += 1;
              changed = true;
              return {
                ...n,
                data: {
                  ...n.data,
                  label: tpl.label,
                  partNumber: tpl.partNumber,
                  manufacturer: tpl.manufacturer,
                  color: tpl.color ?? n.data.color,
                  ...(tpl.mechanical ?? {}),
                },
              };
            }
            return n;
          });
          let edges = sh.edges;
          if (allowedHandlesByNode) {
            const before = edges.length;
            edges = edges.filter((edge) => {
              const sourceAllowed = allowedHandlesByNode?.get(edge.source);
              if (sourceAllowed && !sourceAllowed.has(edge.sourceHandle ?? '')) return false;
              const targetAllowed = allowedHandlesByNode?.get(edge.target);
              if (targetAllowed && !targetAllowed.has(edge.targetHandle ?? '')) return false;
              return true;
            });
            removedEdges += before - edges.length;
            if (edges.length !== before) changed = true;
          }
          return changed ? { ...sh, nodes, edges } : sh;
        });
        if (components || mechanicalItems || removedEdges) set({ sheets });
        return { components, mechanicalItems, removedEdges };
      },

      addAnnotation: (position, text = '') => {
        const node: AnnotationNode = {
          id: nanoid(8),
          type: 'annotation',
          position,
          data: { text, color: '#fef3c7' },
        };
        mutateSheet(get, set, (sh) => ({ ...sh, nodes: [...sh.nodes, node] }));
        set({ selectedNodeId: node.id });
      },

      addImage: (position, src, size, name) => {
        // Photos are a Pro capability. The UI already blocks free users (with a
        // toast), but guard here too so no path can inline-embed images and
        // bloat the autosave / cloud rows for a non-Pro account.
        if (!isPro()) return;
        // Size cap so one huge paste can't bloat the project (and, via the
        // cloud row, the shared DB). Free users never reach here; Pro users get
        // a clear "resize it" nudge rather than a broken save.
        if (src.length > MAX_IMAGE_BASE64_BYTES) {
          showToast('That image is too large (max ~3.5 MB). Please resize it and try again.', 'error', 3200);
          return;
        }
        const width = Math.max(80, Math.round(size?.width ?? 260));
        const height = size?.height ? Math.max(60, Math.round(size.height)) : undefined;
        const node: ImageNode = {
          id: nanoid(8),
          type: 'image',
          position,
          data: { src, name, width, height },
        };
        mutateSheet(get, set, (sh) => ({ ...sh, nodes: [...sh.nodes, node] }));
        set({ selectedNodeId: node.id, selectedEdgeId: null });
      },

      addSystemOverview: () => {
        // Create-or-jump, and top up missing subsystem blocks either way.
        upsertSystemOverview(get, set, true);
      },

      addBlock: (position, title = '') => {
        const node: BlockNode = {
          id: nanoid(8),
          type: 'block',
          position,
          data: {
            title,
            color: '#1e40af',
            textColor: '#ffffff',
            shape: 'rounded',
            width: 200,
            height: 96,
          },
        };
        mutateSheet(get, set, (sh) => ({ ...sh, nodes: [...sh.nodes, node] }));
        set({ selectedNodeId: node.id });
      },

      addMeasurement: (start, end) => {
        const sheet = activeSheet(get());
        const scale =
          sheet.nodes.find(isMechanicalItemNode)?.data.scalePxPerMm ??
          sheet.nodes.find(isMeasurementNode)?.data.scalePxPerMm ??
          1;
        const minX = Math.min(start.x, end.x);
        const minY = Math.min(start.y, end.y);
        const width = Math.max(1, Math.abs(end.x - start.x));
        const height = Math.max(1, Math.abs(end.y - start.y));
        const antiDiagonal = (end.x - start.x) * (end.y - start.y) < 0;
        const node: MeasurementNode = {
          id: nanoid(8),
          type: 'measurement',
          position: { x: minX, y: minY },
          data: {
            width,
            height,
            scalePxPerMm: scale,
            antiDiagonal,
          },
          selectable: true,
          draggable: true,
        };
        mutateSheet(get, set, (sh) => ({ ...sh, nodes: [...sh.nodes, node] }));
        set({ selectedNodeId: node.id, selectedEdgeId: null });
      },

      addPowerSymbol: (position, symbol, name) => {
        const defaultName =
          name ??
          (symbol === 'gnd' ? 'GND' : symbol === 'earth' ? 'EARTH' : symbol === 'vcc' ? 'VCC' : '+24V');
        const node: PowerSymbolNode = {
          id: nanoid(8),
          type: 'powerSymbol',
          position,
          data: { symbol, name: defaultName },
        };
        mutateSheet(get, set, (sh) => ({ ...sh, nodes: [...sh.nodes, node] }));
        set({ selectedNodeId: node.id, selectedEdgeId: null });
      },

      addNetLabel: (position, name = 'NET') => {
        const node: NetLabelNode = {
          id: nanoid(8),
          type: 'netLabel',
          position,
          data: { name },
        };
        mutateSheet(get, set, (sh) => ({ ...sh, nodes: [...sh.nodes, node] }));
        set({ selectedNodeId: node.id, selectedEdgeId: null });
      },

      addSplice: (position) => {
        let selectedId: string | null = null;
        mutateSheet(get, set, (sh) => {
          const count = sh.nodes.filter(isSpliceNode).length + 1;
          const node: SpliceNode = {
            id: nanoid(8),
            type: 'splice',
            position,
            data: { label: `J${count}`, kind: 'splice', notes: '' },
            selected: true,
          };
          selectedId = node.id;
          return {
            ...sh,
            nodes: [...sh.nodes.map((n) => ({ ...n, selected: false })), node],
            edges: sh.edges.map((e) => ({ ...e, selected: false })),
          };
        });
        set({ selectedNodeId: selectedId, selectedEdgeId: null });
      },

      convertEdgeToNetLabels: (edgeId, name) => {
        const cleanName = name.trim() || 'NET';
        mutateSheet(get, set, (sh) => {
          const edge = sh.edges.find((e) => e.id === edgeId);
          if (!edge) return sh;
          const source = sh.nodes.find((n) => n.id === edge.source);
          const target = sh.nodes.find((n) => n.id === edge.target);
          if (!source || !target || !isCompNode(source) || !isCompNode(target)) return sh;
          const conversion = makeNetLabelConversion(edge, source, target, cleanName);
          return {
            ...sh,
            nodes: [
              ...sh.nodes.map((n) => (n.selected ? { ...n, selected: false } : n)),
              ...conversion.labels,
            ],
            edges: [
              ...sh.edges
                .filter((e) => e.id !== edgeId)
                .map((e) => (e.selected ? { ...e, selected: false } : e)),
              ...conversion.links,
            ],
          };
        });
        set({ selectedNodeId: null, selectedEdgeId: null });
      },

      setNetNodeName: (id, name) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          nodes: sh.nodes.map((n) => {
            if (n.id !== id) return n;
            if (isPowerSymbolNode(n)) return { ...n, data: { ...n.data, name } };
            if (isNetLabelNode(n)) return { ...n, data: { ...n.data, name } };
            return n;
          }),
        }));
      },

      addMechanicalItem: (position, item) => {
        const inheritedScale =
          activeSheet(get()).nodes.find(isMechanicalItemNode)?.data.scalePxPerMm ??
          item.scalePxPerMm ??
          1;
        const node: MechanicalItemNode = {
          id: nanoid(8),
          type: 'mechanicalItem',
          position,
          data: {
            ...item,
            label: item.label || 'Mechanical item',
            widthMm: item.widthMm ?? 100,
            heightMm: item.heightMm ?? 60,
            mounting: item.mounting ?? 'free',
            orientation: item.orientation ?? 'top',
            color: item.color ?? '#64748b',
            scalePxPerMm: inheritedScale,
            labelOffsetX: item.labelOffsetX,
            labelOffsetY: item.labelOffsetY,
            rotation: item.rotation ?? 0,
          },
        };
        mutateSheet(get, set, (sh) => {
          const nextNodes =
            node.data.mounting === 'chassis' ? [node, ...sh.nodes] : [...sh.nodes, node];
          return { ...sh, nodes: withLayerIndexes(nextNodes) };
        });
        set({ selectedNodeId: node.id, selectedEdgeId: null });
      },

      setAnnotationText: (id, text) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          nodes: sh.nodes.map((n) =>
            n.id === id && isAnnotationNode(n) ? { ...n, data: { ...n.data, text } } : n,
          ),
        }));
      },

      setAnnotationField: (id, patch) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          nodes: sh.nodes.map((n) =>
            n.id === id && isAnnotationNode(n) ? { ...n, data: { ...n.data, ...patch } } : n,
          ),
        }));
      },

      setImageField: (id, patch) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          nodes: sh.nodes.map((n) =>
            n.id === id && isImageNode(n) ? { ...n, data: { ...n.data, ...patch } } : n,
          ),
        }));
      },

      setBlockField: (id, patch) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          nodes: sh.nodes.map((n) =>
            n.id === id && isBlockNode(n) ? { ...n, data: { ...n.data, ...patch } } : n,
          ),
        }));
      },

      setMechanicalItemField: (id, patch) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          nodes: sh.nodes.map((n) =>
            n.id === id && isMechanicalItemNode(n)
              ? { ...n, data: { ...n.data, ...patch } }
              : n,
          ),
        }));
      },

      setMechanicalSheetScale: (scalePxPerMm) => {
        const nextScale = Math.max(0.1, scalePxPerMm || 1);
        mutateSheet(get, set, (sh) => ({
          ...sh,
          nodes: sh.nodes.map((n) =>
            isMechanicalItemNode(n)
              ? { ...n, data: { ...n.data, scalePxPerMm: nextScale } }
              : n,
          ),
        }));
      },

      addRequirement: (seed) => {
        mutateSheet(get, set, (sh) => {
          const list = sh.requirements ?? [];
          const nextNumber = `R${list.length + 1}`;
          const req: Requirement = {
            id: nanoid(8),
            reqNumber: seed?.reqNumber ?? nextNumber,
            category: seed?.category ?? 'Functional',
            priority: seed?.priority ?? 'Must',
            status: seed?.status ?? 'Draft',
            title: seed?.title ?? '',
            description: seed?.description ?? '',
            verification: seed?.verification ?? '',
            notes: seed?.notes ?? '',
          };
          return { ...sh, requirements: [...list, req] };
        });
      },

      updateRequirement: (id, patch) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          requirements: (sh.requirements ?? []).map((r) =>
            r.id === id ? { ...r, ...patch } : r,
          ),
        }));
      },

      removeRequirement: (id) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          requirements: (sh.requirements ?? []).filter((r) => r.id !== id),
        }));
      },

      addBundle: (seed) => {
        mutateSheet(get, set, (sh) => {
          const list = sh.bundles ?? [];
          const bundle: InterconnectBundle = {
            id: nanoid(8),
            name: seed?.name ?? `Bundle ${list.length + 1}`,
            fromUnit: seed?.fromUnit ?? '',
            toUnit: seed?.toUnit ?? '',
            conduit: seed?.conduit ?? '',
            entryFrom: seed?.entryFrom ?? '',
            entryTo: seed?.entryTo ?? '',
            cables: seed?.cables ?? '',
            length: seed?.length ?? '',
            endAConnector: seed?.endAConnector ?? '',
            endBConnector: seed?.endBConnector ?? '',
            endATermination: seed?.endATermination ?? '',
            endBTermination: seed?.endBTermination ?? '',
            branchLayout: seed?.branchLayout ?? '',
            productionNotes: seed?.productionNotes ?? '',
            drawingNumber: seed?.drawingNumber ?? '',
            revision: seed?.revision ?? 'A',
            revisionDate: seed?.revisionDate ?? '',
            drawnBy: seed?.drawnBy ?? get().meta.author,
            checkedBy: seed?.checkedBy ?? '',
            approvedBy: seed?.approvedBy ?? '',
            qaBy: seed?.qaBy,
            revisions: seed?.revisions,
            graph: seed?.graph,
            drawingItems: seed?.drawingItems ?? [],
            notes: seed?.notes ?? '',
          };
          return { ...sh, bundles: [...list, bundle] };
        });
      },

      updateBundle: (id, patch) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          bundles: (sh.bundles ?? []).map((b) => (b.id === id ? { ...b, ...patch } : b)),
        }));
      },

      removeBundle: (id) => {
        // Content edit, not a sheet deletion — the read-only escape hatch only
        // covers deleting sheets/subsystems (to shrink under the free limit).
        if (editLocked(get())) return;
        // Remove the bundle AND clear its id from every wire that referenced it
        // (across all sheets), so no edge is left pointing at a dead harness.
        set({
          sheets: get().sheets.map((sh) => {
            let next = sh;
            if (sh.kind === 'interconnect' && (sh.bundles ?? []).some((b) => b.id === id)) {
              next = { ...next, bundles: (next.bundles ?? []).filter((b) => b.id !== id) };
            }
            if (sh.edges.some((e) => e.data?.harnessId === id)) {
              next = {
                ...next,
                edges: next.edges.map((e) =>
                  e.data?.harnessId === id
                    ? { ...e, data: { ...(e.data ?? {}), harnessId: undefined } }
                    : e,
                ),
              };
            }
            return next;
          }),
        });
      },

      moveBundle: (id, dir) => {
        mutateSheet(get, set, (sh) => {
          const list = [...(sh.bundles ?? [])];
          const i = list.findIndex((b) => b.id === id);
          if (i === -1) return sh;
          const target = i + dir;
          if (target < 0 || target >= list.length) return sh;
          [list[i], list[target]] = [list[target], list[i]];
          return { ...sh, bundles: list };
        });
      },

      addSection: (seed) => {
        mutateSheet(get, set, (sh) => {
          const list = sh.sections ?? [];
          const section: DocumentSection = {
            id: nanoid(8),
            heading: seed?.heading ?? `Section ${list.length + 1}`,
            body: seed?.body ?? '',
          };
          return { ...sh, sections: [...list, section] };
        });
      },

      updateSection: (id, patch) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          sections: (sh.sections ?? []).map((s) => (s.id === id ? { ...s, ...patch } : s)),
        }));
      },

      removeSection: (id) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          sections: (sh.sections ?? []).filter((s) => s.id !== id),
        }));
      },

      moveSection: (id, dir) => {
        mutateSheet(get, set, (sh) => {
          const list = [...(sh.sections ?? [])];
          const i = list.findIndex((s) => s.id === id);
          if (i === -1) return sh;
          const target = i + dir;
          if (target < 0 || target >= list.length) return sh;
          [list[i], list[target]] = [list[target], list[i]];
          return { ...sh, sections: list };
        });
      },

      setDocumentCategory: (category) => {
        mutateSheet(get, set, (sh) => ({ ...sh, documentCategory: category }));
      },

      moveRequirement: (id, dir) => {
        mutateSheet(get, set, (sh) => {
          const list = [...(sh.requirements ?? [])];
          const i = list.findIndex((r) => r.id === id);
          if (i === -1) return sh;
          const target = i + dir;
          if (target < 0 || target >= list.length) return sh;
          [list[i], list[target]] = [list[target], list[i]];
          return { ...sh, requirements: list };
        });
      },

      selectNode: (id) => set({ selectedNodeId: id, selectedEdgeId: null }),
      selectEdge: (id) => set({ selectedEdgeId: id, selectedNodeId: null }),

      focusHarness: (id) => {
        if (!id) {
          set({ focusedHarnessId: null });
          return;
        }
        // When entering focus mode, hop to the schematic where the user can
        // see individual conductor highlighting. The block diagram only
        // shows ONE line per harness, so jumping there hides the very thing
        // focus mode is meant to reveal. Prefer schematic; fall back to
        // block only if no schematic carries this harness.
        const current = get();
        const schematicHit = current.sheets.find(
          (sh) =>
            sh.kind === 'schematic' &&
            sh.edges.some((e) => e.data?.harnessId === id),
        );
        const blockHit = current.sheets.find(
          (sh) =>
            sh.kind === 'block' &&
            sh.edges.some((e) => e.data?.harnessId === id),
        );
        const target = schematicHit ?? blockHit;
        if (target && target.id !== current.activeSheetId) {
          set({
            focusedHarnessId: id,
            activeSheetId: target.id,
            selectedNodeId: null,
            selectedEdgeId: null,
          });
        } else {
          set({ focusedHarnessId: id });
        }
      },

      focusNet: (net) => set({ focusedNet: net, focusedHarnessId: null }),
      setFocusedNetMode: (mode) => set({ focusedNetMode: mode }),
      setWireFilter: (f) => set({ wireFilter: f }),

      autoAssignNetNamesFromWireLabels: () => {
        if (editLocked(get())) return { updated: 0, groups: [] };
        const counts = new Map<string, number>();
        let updated = 0;
        const sheets = get().sheets.map((sh) => {
          if (sh.kind !== 'schematic') return sh;
          let changed = false;
          const nextNodes = [...sh.nodes];
          const nextEdges: WireEdge[] = [];
          const nodesById = new Map(sh.nodes.map((node) => [node.id, node]));
          for (const edge of sh.edges) {
            // Never convert a wire that belongs to a harness — the resulting
            // net-label stub would replace the real component pin endpoint and
            // corrupt the harness wire list.
            if (edge.data?.harnessId) {
              nextEdges.push(edge);
              continue;
            }
            const edgeLabel = typeof edge.label === 'string' ? edge.label : undefined;
            const normalized = normalizeAutoWireNetName(edge.data?.label || edgeLabel);
            if (!normalized) {
              nextEdges.push(edge);
              continue;
            }
            counts.set(normalized, (counts.get(normalized) ?? 0) + 1);
            const source = nodesById.get(edge.source);
            const target = nodesById.get(edge.target);
            if (source && target && isCompNode(source) && isCompNode(target)) {
              const conversion = makeNetLabelConversion(edge, source, target, normalized);
              nextNodes.push(...conversion.labels.map((node) => ({ ...node, selected: false })));
              nextEdges.push(...conversion.links.map((link) => ({ ...link, selected: false })));
              updated += 1;
              changed = true;
              continue;
            }
            if (edge.data?.netName === normalized) {
              nextEdges.push(edge);
              continue;
            }
            updated += 1;
            changed = true;
            nextEdges.push({
              ...edge,
              data: {
                ...(edge.data ?? {}),
                netName: normalized,
                label: '',
              },
              label: '',
            } as WireEdge);
          }
          return changed
            ? {
                ...sh,
                nodes: nextNodes,
                edges: nextEdges,
              }
            : sh;
        });
        if (updated > 0) set({ sheets });
        return {
          updated,
          groups: [...counts.entries()]
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)),
        };
      },

      updateNodeData: (id, patch) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          nodes: sh.nodes.map((n) => {
            if (n.id !== id || !isCompNode(n)) return n;
            return { ...n, data: { ...n.data, ...patch } };
          }),
        }));
      },

      updatePin: (nodeId, pinId, patch) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          nodes: sh.nodes.map((n) => {
            if (n.id !== nodeId || !isCompNode(n)) return n;
            return {
              ...n,
              data: {
                ...n.data,
                pins: n.data.pins.map((p) => (p.id === pinId ? { ...p, ...patch } : p)),
              },
            };
          }),
        }));
      },

      addPin: (nodeId, pin) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          nodes: sh.nodes.map((n) => {
            if (n.id !== nodeId || !isCompNode(n)) return n;
            return {
              ...n,
              data: { ...n.data, pins: [...n.data.pins, { ...pin, id: nanoid(6) }] },
            };
          }),
        }));
      },

      removePin: (nodeId, pinId) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          nodes: sh.nodes.map((n) => {
            if (n.id !== nodeId || !isCompNode(n)) return n;
            return {
              ...n,
              data: { ...n.data, pins: n.data.pins.filter((p) => p.id !== pinId) },
            };
          }),
          edges: sh.edges.filter(
            (e) =>
              !(e.source === nodeId && e.sourceHandle === pinId) &&
              !(e.target === nodeId && e.targetHandle === pinId),
          ),
        }));
      },

      rotateNode: (nodeId, deltaDeg = 90) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          nodes: sh.nodes.map((n) => {
            if (n.id !== nodeId) return n;
            if (isCompNode(n)) {
              return {
                ...n,
                data: {
                  ...n.data,
                  rotation: ((((n.data.rotation ?? 0) + deltaDeg) % 360) + 360) % 360,
                },
              };
            }
            if (isMechanicalItemNode(n)) {
              return {
                ...n,
                data: {
                  ...n.data,
                  rotation: ((((n.data.rotation ?? 0) + deltaDeg) % 360) + 360) % 360,
                },
              };
            }
            return n;
          }),
        }));
      },

      renameGroup: (nodeId, oldName, newName) => {
        const trimmed = newName.trim();
        mutateSheet(get, set, (sh) => ({
          ...sh,
          nodes: sh.nodes.map((n) => {
            if (n.id !== nodeId || !isCompNode(n)) return n;
            return {
              ...n,
              data: {
                ...n.data,
                pins: n.data.pins.map((p) =>
                  (p.group ?? '') === oldName
                    ? { ...p, group: trimmed ? trimmed : undefined }
                    : p,
                ),
              },
            };
          }),
        }));
      },

      updateEdgeData: (id, patch) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          edges: sh.edges.map((e) =>
            e.id === id
              ? { ...e, data: { ...(e.data ?? {}), ...patch }, label: patch.label ?? e.label }
              : e,
          ),
        }));
      },

      assignHarnessToEdges: (edgeIds, harnessId) => {
        if (edgeIds.length === 0) return;
        const idSet = new Set(edgeIds);
        mutateSheet(get, set, (sh) => ({
          ...sh,
          edges: sh.edges.map((e) =>
            idSet.has(e.id)
              ? { ...e, data: { ...(e.data ?? {}), harnessId } }
              : e,
          ),
        }));
      },

      assignHarnessToEdgesAcrossSheets: (edgeIds, harnessId) => {
        // Cross-sheet mutations bypass mutateSheet, so they need their own
        // read-only guard (free account viewing an over-limit project).
        if (editLocked(get())) return;
        if (edgeIds.length === 0) return;
        const idSet = new Set(edgeIds);
        set({
          sheets: get().sheets.map((sh) => ({
            ...sh,
            edges: sh.edges.map((e) =>
              idSet.has(e.id)
                ? { ...e, data: { ...(e.data ?? {}), harnessId } }
                : e,
            ),
          })),
        });
      },

      createHarnessForEdges: (edgeIds) => {
        if (editLocked(get())) return null;
        if (edgeIds.length === 0) return null;
        const state = get();
        const prefix = inferHarnessPrefixForEdges(state.sheets, edgeIds);
        const used = new Set<string>();
        for (const sh of state.sheets) {
          for (const bundle of sh.bundles ?? []) used.add(bundle.name);
        }
        const id = nanoid(8);
        const name = nextHarnessName(prefix, used);
        const bundle: InterconnectBundle = {
          id,
          name,
          fromUnit: '',
          toUnit: '',
          conduit: '',
          entryFrom: '',
          entryTo: '',
          cables: '',
          length: '',
          endAConnector: '',
          endBConnector: '',
          endATermination: '',
          endBTermination: '',
          branchLayout: '',
          productionNotes: '',
          drawingNumber: '',
          revision: 'A',
          revisionDate: '',
          drawnBy: state.meta.author,
          checkedBy: '',
          approvedBy: '',
          drawingItems: [],
          notes: '',
        };
        const interconnectIndex = state.sheets.findIndex((sh) => sh.kind === 'interconnect');
        const targetSheetId =
          interconnectIndex >= 0 ? state.sheets[interconnectIndex].id : nanoid(8);
        const idSet = new Set(edgeIds);
        const nextSheets =
          interconnectIndex >= 0
            ? state.sheets.map((sh) =>
                sh.id === targetSheetId
                  ? { ...sh, bundles: [...(sh.bundles ?? []), bundle] }
                  : sh,
              )
            : [
                {
                  id: targetSheetId,
                  name: 'Harnesses',
                  kind: 'interconnect' as const,
                  nodes: [],
                  edges: [],
                  bundles: [bundle],
                },
                ...state.sheets,
              ];
        set({
          sheets: nextSheets.map((sh) => ({
            ...sh,
            edges: sh.edges.map((e) =>
              idSet.has(e.id) ? { ...e, data: { ...(e.data ?? {}), harnessId: id } } : e,
            ),
          })),
        });
        return id;
      },

      normalizeHarnessNames: () => {
        if (editLocked(get())) return 0;
        const state = get();
        const bundleIds = new Set<string>();
        for (const sh of state.sheets) {
          for (const bundle of sh.bundles ?? []) bundleIds.add(bundle.id);
        }
        const ordered = [...bundleIds].sort((a, b) => {
          const pa = inferHarnessPrefixForEdges(
            state.sheets,
            state.sheets.flatMap((sh) =>
              sh.edges.filter((e) => e.data?.harnessId === a).map((e) => e.id),
            ),
          );
          const pb = inferHarnessPrefixForEdges(
            state.sheets,
            state.sheets.flatMap((sh) =>
              sh.edges.filter((e) => e.data?.harnessId === b).map((e) => e.id),
            ),
          );
          return pa.localeCompare(pb) || harnessSortKey(state.sheets, a).localeCompare(harnessSortKey(state.sheets, b));
        });
        const counters = new Map<string, number>();
        const names = new Map<string, string>();
        for (const id of ordered) {
          const edgeIds = state.sheets.flatMap((sh) =>
            sh.edges.filter((e) => e.data?.harnessId === id).map((e) => e.id),
          );
          const prefix = inferHarnessPrefixForEdges(state.sheets, edgeIds);
          const next = (counters.get(prefix) ?? 0) + 1;
          counters.set(prefix, next);
          names.set(id, makeHarnessName(prefix, next));
        }
        let renamed = 0;
        set({
          sheets: state.sheets.map((sh) => ({
            ...sh,
            bundles: (sh.bundles ?? []).map((bundle) => {
              const name = names.get(bundle.id);
              if (!name || bundle.name === name) return bundle;
              renamed += 1;
              return { ...bundle, name };
            }),
          })),
        });
        return renamed;
      },

      updateEdgesAcrossSheets: (edgeIds, patch) => {
        if (editLocked(get())) return;
        if (edgeIds.length === 0) return;
        const idSet = new Set(edgeIds);
        set({
          sheets: get().sheets.map((sh) => ({
            ...sh,
            edges: sh.edges.map((e) =>
              idSet.has(e.id) ? { ...e, data: { ...(e.data ?? {}), ...patch } } : e,
            ),
          })),
        });
      },

      autoNumberWires: () => {
        if (editLocked(get())) return 0;
        // highest existing W<number> so we continue, not collide
        let max = 0;
        for (const sh of get().sheets) {
          if (sh.kind !== 'schematic') continue;
          for (const e of sh.edges) {
            const m = /^W(\d+)$/.exec(String(e.data?.wireNo ?? ''));
            if (m) max = Math.max(max, Number(m[1]));
          }
        }
        let n = max;
        let numbered = 0;
        const sheets = get().sheets.map((sh) => {
          if (sh.kind !== 'schematic') return sh;
          let changed = false;
          const edges = sh.edges.map((e) => {
            if (e.data?.wireNo) return e;
            n += 1;
            numbered += 1;
            changed = true;
            return { ...e, data: { ...(e.data ?? {}), wireNo: `W${n}` } };
          });
          return changed ? { ...sh, edges } : sh;
        });
        if (numbered > 0) set({ sheets });
        return numbered;
      },

      syncFromRemote: (meta, sheets) => {
        set({ meta, sheets });
      },

      goToEdge: (edgeId) => {
        const current = get();
        const owner = current.sheets.find((sh) =>
          sh.edges.some((e) => e.id === edgeId),
        );
        if (!owner) return;
        const updatedSheets = current.sheets.map((sh) =>
          sh.id === owner.id
            ? {
                ...sh,
                edges: sh.edges.map((e) => ({
                  ...e,
                  selected: e.id === edgeId,
                })),
                nodes: sh.nodes.map((n) =>
                  n.selected ? { ...n, selected: false } : n,
                ),
              }
            : sh,
        );
        set({
          sheets: updatedSheets,
          activeSheetId: owner.id,
          selectedEdgeId: edgeId,
          selectedNodeId: null,
        });
      },

      moveNodeLayer: (id, mode) => {
        mutateSheet(get, set, (sh) => {
          const index = sh.nodes.findIndex((n) => n.id === id);
          if (index === -1) return sh;
          const nodes = [...sh.nodes];
          const [node] = nodes.splice(index, 1);
          let target = index;
          if (mode === 'front') target = nodes.length;
          else if (mode === 'back') target = 0;
          else if (mode === 'forward') target = Math.min(nodes.length, index + 1);
          else if (mode === 'backward') target = Math.max(0, index - 1);
          nodes.splice(target, 0, node);
          return { ...sh, nodes: withLayerIndexes(nodes) };
        });
      },

      deleteNode: (id) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          nodes: sh.nodes.filter((n) => n.id !== id),
          edges: sh.edges.filter((e) => e.source !== id && e.target !== id),
        }));
        set({ selectedNodeId: null });
      },

      deleteEdge: (id) => {
        mutateSheet(get, set, (sh) => ({
          ...sh,
          edges: sh.edges.filter((e) => e.id !== id),
        }));
        set({ selectedEdgeId: null });
      },

      updateMeta: (patch) => {
        if (editLocked(get())) return;
        set({ meta: { ...get().meta, ...patch, updatedAt: new Date().toISOString() } });
      },

      setProject: ({ meta, sheets, activeSheetId }) => {
        const normalized = ensureSheetsCoverage(sheets);
        // Backfill the between-subsystems kit for projects that predate it
        // (old saves / shared files with ≥2 subsystems). One-time, additive.
        const kit = ensureSystemKit(normalized, meta);
        // Validate the requested active sheet ID against the normalised list —
        // older project files (or autosaves from a previous schema) may carry
        // a stale ID that no longer exists, which would land the canvas on
        // an undefined sheet and blank out the screen.
        const validActiveId = kit.sheets.find((sh) => sh.id === activeSheetId)
          ? activeSheetId
          : kit.sheets[0]?.id;
        set({
          meta: kit.meta,
          sheets: kit.sheets,
          activeSheetId: validActiveId,
          selectedNodeId: null,
          selectedEdgeId: null,
          focusedHarnessId: null,
          focusedNet: null,
          focusedNetMode: 'dim',
        });
        useStore.temporal.getState().clear();
      },

      newProject: () => {
        const fresh = defaultSheets();
        set({
          meta: emptyMeta(),
          sheets: fresh,
          activeSheetId: fresh[0]?.id ?? '',
          selectedNodeId: null,
          selectedEdgeId: null,
          focusedHarnessId: null,
          focusedNet: null,
          focusedNetMode: 'dim',
        });
        useStore.temporal.getState().clear();
      },

      copySelectedNode: () => {
        const sheet = activeSheet(get());
        const selectedNodes = sheet.nodes.filter((n) => n.selected);
        const nodes =
          selectedNodes.length > 0
            ? selectedNodes
            : get().selectedNodeId
              ? sheet.nodes.filter((n) => n.id === get().selectedNodeId)
              : [];
        if (nodes.length === 0) return false;
        const nodeIds = new Set(nodes.map((n) => n.id));
        const selectedEdges = sheet.edges.filter((e) => e.selected);
        const edges = [
          ...selectedEdges,
          ...sheet.edges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target)),
        ].filter((e, i, all) => all.findIndex((x) => x.id === e.id) === i);
        CLIPBOARD_GRAPH = {
          nodes: nodes.map((n) => ({ ...n, data: { ...n.data } }) as AnyNode),
          edges: edges.map((e) => ({ ...e, data: { ...(e.data ?? {}) } })),
        };
        return true;
      },

      pasteClipboard: (position) => {
        if (!CLIPBOARD_GRAPH || CLIPBOARD_GRAPH.nodes.length === 0) return false;
        const minX = Math.min(...CLIPBOARD_GRAPH.nodes.map((n) => n.position.x));
        const minY = Math.min(...CLIPBOARD_GRAPH.nodes.map((n) => n.position.y));
        const anchor = position ?? get().lastPastePosition ?? { x: minX + 40, y: minY + 40 };
        const dx = anchor.x - minX;
        const dy = anchor.y - minY;
        const nodeIdMap = new Map<string, string>();
        const pinIdMap = new Map<string, Map<string, string>>();
        const newNodes = CLIPBOARD_GRAPH.nodes.map((node) =>
          cloneNodeForPaste(
            node,
            {
              x: anchor.x + (node.position.x - minX),
              y: anchor.y + (node.position.y - minY),
            },
            nodeIdMap,
            pinIdMap,
          ),
        );
        const newEdges: WireEdge[] = CLIPBOARD_GRAPH.edges
          .map((edge) => {
            const source = nodeIdMap.get(edge.source);
            const target = nodeIdMap.get(edge.target);
            if (!source || !target) return null;
            const sourceHandle =
              edge.sourceHandle && pinIdMap.get(edge.source)?.get(edge.sourceHandle)
                ? pinIdMap.get(edge.source)?.get(edge.sourceHandle)
                : edge.sourceHandle;
            const targetHandle =
              edge.targetHandle && pinIdMap.get(edge.target)?.get(edge.targetHandle)
                ? pinIdMap.get(edge.target)?.get(edge.targetHandle)
                : edge.targetHandle;
            return {
              ...edge,
              id: nanoid(8),
              source,
              target,
              sourceHandle,
              targetHandle,
              selected: true,
              data: {
                ...(edge.data ?? {}),
                midX:
                  typeof edge.data?.midX === 'number'
                    ? edge.data.midX + dx
                    : edge.data?.midX,
                midY:
                  typeof edge.data?.midY === 'number'
                    ? edge.data.midY + dy
                    : edge.data?.midY,
                midX2:
                  typeof edge.data?.midX2 === 'number'
                    ? edge.data.midX2 + dx
                    : edge.data?.midX2,
                midY2:
                  typeof edge.data?.midY2 === 'number'
                    ? edge.data.midY2 + dy
                    : edge.data?.midY2,
              },
            } as WireEdge;
          })
          .filter((e): e is WireEdge => e !== null);
        mutateSheet(get, set, (sh) => ({
          ...sh,
          nodes: [
            ...sh.nodes.map((n) => (n.selected ? { ...n, selected: false } : n)),
            ...newNodes,
          ],
          edges: [
            ...sh.edges.map((e) => (e.selected ? { ...e, selected: false } : e)),
            ...newEdges,
          ],
        }));
        set({
          selectedNodeId: newNodes.length === 1 ? newNodes[0].id : null,
          selectedEdgeId: null,
        });
        CLIPBOARD_GRAPH = { nodes: newNodes, edges: newEdges };
        return true;
      },

      hasClipboard: () => CLIPBOARD_GRAPH !== null,

      alignNodes: (mode) => {
        const sheet = activeSheet(get());
        const selected = sheet.nodes.filter((n) => n.selected);
        if (selected.length < 2) return;
        const anchor = (() => {
          switch (mode) {
            case 'left':
              return Math.min(...selected.map((n) => n.position.x));
            case 'right':
              return Math.max(...selected.map((n) => n.position.x + (n.measured?.width ?? 200)));
            case 'top':
              return Math.min(...selected.map((n) => n.position.y));
            case 'bottom':
              return Math.max(...selected.map((n) => n.position.y + (n.measured?.height ?? 100)));
            case 'center-h': {
              const sum = selected.reduce(
                (s, n) => s + n.position.x + (n.measured?.width ?? 200) / 2,
                0,
              );
              return sum / selected.length;
            }
            case 'center-v': {
              const sum = selected.reduce(
                (s, n) => s + n.position.y + (n.measured?.height ?? 100) / 2,
                0,
              );
              return sum / selected.length;
            }
          }
        })();
        const selectedIds = new Set(selected.map((n) => n.id));
        mutateSheet(get, set, (sh) => ({
          ...sh,
          nodes: sh.nodes.map((n) => {
            if (!selectedIds.has(n.id)) return n;
            const w = n.measured?.width ?? 200;
            const h = n.measured?.height ?? 100;
            let x = n.position.x;
            let y = n.position.y;
            switch (mode) {
              case 'left':
                x = anchor;
                break;
              case 'right':
                x = anchor - w;
                break;
              case 'top':
                y = anchor;
                break;
              case 'bottom':
                y = anchor - h;
                break;
              case 'center-h':
                x = anchor - w / 2;
                break;
              case 'center-v':
                y = anchor - h / 2;
                break;
            }
            return { ...n, position: { x, y } };
          }),
        }));
      },

      distributeNodes: (axis) => {
        const sheet = activeSheet(get());
        const selected = sheet.nodes.filter((n) => n.selected);
        if (selected.length < 3) return;
        const sorted = [...selected].sort((a, b) =>
          axis === 'h' ? a.position.x - b.position.x : a.position.y - b.position.y,
        );
        const first = sorted[0];
        const last = sorted[sorted.length - 1];
        const firstCenter =
          axis === 'h'
            ? first.position.x + (first.measured?.width ?? 200) / 2
            : first.position.y + (first.measured?.height ?? 100) / 2;
        const lastCenter =
          axis === 'h'
            ? last.position.x + (last.measured?.width ?? 200) / 2
            : last.position.y + (last.measured?.height ?? 100) / 2;
        const step = (lastCenter - firstCenter) / (sorted.length - 1);
        const byId = new Map(sorted.map((n, i) => [n.id, i]));
        mutateSheet(get, set, (sh) => ({
          ...sh,
          nodes: sh.nodes.map((n) => {
            const i = byId.get(n.id);
            if (i === undefined) return n;
            if (i === 0 || i === sorted.length - 1) return n;
            const targetCenter = firstCenter + i * step;
            const w = n.measured?.width ?? 200;
            const h = n.measured?.height ?? 100;
            if (axis === 'h') {
              return { ...n, position: { x: targetCenter - w / 2, y: n.position.y } };
            }
            return { ...n, position: { x: n.position.x, y: targetCenter - h / 2 } };
          }),
        }));
      },
    }),
    {
      // Track the document — meta + sheets. UI-only fields (active sheet, selection)
      // are intentionally excluded so sheet switching is not part of undo.
      partialize: (state) => ({
        meta: state.meta,
        sheets: state.sheets,
      }),
      limit: 200,
      handleSet: (handleSet) => debounceFirstHandleSet(handleSet, 700),
      equality: (a, b) => a.meta === b.meta && a.sheets === b.sheets,
    },
  ),
);
